import * as React from "react";
import { observer } from "mobx-react";
import { observable, action } from "mobx";
import { LinksProps } from "./typings";
import {
  UitkCard,
  UitkCardTextSegment,
  UitkCardContent,
  UitkCardSectionSeparatorType
} from "uitk-react-cards";
import { UitkIcon } from "uitk-react-icons";
import { UitkLink } from "uitk-react-link";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { DefaultFlexModuleResult, Link } from "typings/microserviceModels/links-flex-module";
import { LocalizedText } from "bernie-l10n";
import { GeoType } from "src/typings/flexFramework/FlexDefinitions";
import { LocationType } from "src/components/utility/LocationUtils";

function getUitkLink(model: DefaultFlexModuleResult, el: Link) {
  const text = `${el.text} ${(el.auxiliaryText && `(${el.auxiliaryText})`) || ""}`;

  if (!el.url) {
    return <span className="truncate uitk-link-layout-default uitk-type-300">{text}</span>;
  }

  return (
    <UitkLink className="truncate">
      <TrackedLink moduleName={model.name} href={el.url} rfrr={model.strategy} linkName={el.text}>
        {text}
      </TrackedLink>
    </UitkLink>
  );
}

/**
 * Card with links. Handled three different ways. Either it will display
 * auxiliary content in parenthesis, with an additional card to see more info,
 * or none of the former.
 *
 * Mock flexComponent prop received: @see {.offline/viewModels/Links.json}
 *
 */

@observer
export class Links extends React.Component<LinksProps, {}> {
  @observable
  public toggle: boolean;

  constructor(props: LinksProps) {
    super(props);
    this.toggle = props.flexComponent.model.toggle === "collapsed" ? true : false;
  }

  @action
  public changeToggle = () => {
    this.toggle = !this.toggle;
  };

  public render() {
    const props = this.props;
    const { flexComponent, isInsideTabGroup } = props;
    const model = flexComponent.model;

    if (!flexComponent || !model || !model.links || model.links.length === 0) {
      return null;
    }

    const context = props.context;
    const location = context.searchContext.location ? context.searchContext.location.name : null;
    const locationType = context.searchContext.location && context.searchContext.location.type;
    const keyHelper = new ItemKeyHelper("links");

    const paddingClassName = isInsideTabGroup ? "" : "all-t-padding-three";

    return (
      <div
        className={`Links uitk-cell all-cell-1-1 ${paddingClassName}`}
        id={flexComponent.id}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
      >
        <UitkCard>
          <UitkCardContent>
            {!isInsideTabGroup && (
              <div
                role="button"
                aria-expanded={!this.toggle}
                className="header"
                onClick={this.changeToggle}
              >
                <UitkCardTextSegment
                  className={`uitk-flex uitk-flex-align-items-center all-y-padding-four ${this
                    .toggle && "allBorderZero"}`}
                  separator={UitkCardSectionSeparatorType.BOTTOM}
                >
                  <h3 className="uitk-type-heading-500">{model.title}</h3>
                  <UitkIcon name={`expand_${this.toggle ? "more" : "less"}`} />
                </UitkCardTextSegment>
              </div>
            )}
            <ul
              className={`uitk-cell all-x-padding-three no-bullet ${
                this.toggle && !isInsideTabGroup
                  ? "all-y-padding-three is-visually-hidden"
                  : "all-y-padding-three"
              }`}
            >
              {model.links.map(el => (
                <li key={keyHelper.next()}>{getUitkLink(model, el)}</li>
              ))}
            </ul>
            {model.seeAllHotelLinks &&
              location && (
                <UitkCardTextSegment
                  separator={UitkCardSectionSeparatorType.TOP}
                  className="seeAllSection"
                >
                  <SeeAllContentLink
                    model={model}
                    location={location}
                    locationType={locationType}
                  />
                </UitkCardTextSegment>
              )}
          </UitkCardContent>
        </UitkCard>
      </div>
    );
  }
}

const SeeAllContentLink = ({
  model,
  location,
  locationType
}: {
  model: DefaultFlexModuleResult;
  location: string;
  locationType?: GeoType;
}) => {
  const useNearPreposition =
    locationType &&
    (locationType === LocationType.AIRPORT ||
      locationType === LocationType.POINT_OF_INTEREST ||
      locationType === LocationType.METRO_STATION ||
      locationType === LocationType.TRAIN_STATION);

  const linksMessage = useNearPreposition ? "links.seeAllHotelsNear" : "links.seeAllHotels";

  return (
    <UitkLink>
      <TrackedLink
        moduleName={model.name}
        href={model.seeAllHotelLinks}
        rfrr={model.strategy}
        linkName={model.seeAllHotelLinks}
      >
        <LocalizedText message={linksMessage} data={[location]} />
      </TrackedLink>
    </UitkLink>
  );
};

export default Links;
